<template>
  <div class="friends-tabs-container">

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <div class="connect-header">
      <div class="connect-header-copy">
        <span class="connect-eyebrow">
          Ваши связи
        </span>

        <h2 class="connect-title">
          Друзья
        </h2>

        <p class="connect-description">
          Общайтесь, добавляйте полезные контакты и расширяйте свой круг.
        </p>
      </div>

      <div class="connect-total">
        <div class="connect-total-value">
          {{ friends.length }}
        </div>

        <div class="connect-total-label">
          контактов
        </div>
      </div>
    </div>


    <!-- =====================================================
         TABS
    ====================================================== -->

    <div class="tabs-header">

      <div
        class="tab-indicator"
        :class="activeTab"
      ></div>


      <!-- FRIENDS -->

      <button
        type="button"
        class="tab-button"
        :class="{
          active:
            activeTab === 'friends'
        }"
        @click="activeTab = 'friends'"
      >
        <span class="tab-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="9"
              cy="8"
              r="3"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M4.5 18C5.1 14.9 6.7 13.2 9 13.2C11.3 13.2 12.9 14.9 13.5 18"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />

            <path
              d="M15.1 6.1C17 6.25 18.35 7.55 18.35 9.2C18.35 10.65 17.4 11.85 15.95 12.25"
              stroke="currentColor"
              stroke-width="1.45"
              stroke-linecap="round"
            />

            <path
              d="M16.25 14C18.1 14.45 19.3 15.8 19.7 18"
              stroke="currentColor"
              stroke-width="1.45"
              stroke-linecap="round"
            />
          </svg>
        </span>

        <span class="tab-copy">
          <span class="tab-title">
            Все друзья
          </span>

          <span class="tab-subtitle">
            Ваши контакты
          </span>
        </span>

        <span class="tab-count">
          {{ friends.length }}
        </span>
      </button>


      <!-- REQUESTS -->

      <button
        type="button"
        class="tab-button"
        :class="{
          active:
            activeTab === 'requests'
        }"
        @click="activeTab = 'requests'"
      >
        <span class="tab-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 7.5C5 6.12 6.12 5 7.5 5H16.5C17.88 5 19 6.12 19 7.5V16.5C19 17.88 17.88 19 16.5 19H7.5C6.12 19 5 17.88 5 16.5V7.5Z"
              stroke="currentColor"
              stroke-width="1.55"
            />

            <path
              d="M7.5 8L12 11.2L16.5 8"
              stroke="currentColor"
              stroke-width="1.55"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        <span class="tab-copy">
          <span class="tab-title">
            Запросы
          </span>

          <span class="tab-subtitle">
            Новые знакомства
          </span>
        </span>

        <span
          class="tab-count"
          :class="{
            attention:
              requests.length > 0
          }"
        >
          {{ requests.length }}
        </span>
      </button>

    </div>


    <!-- =====================================================
         CONTENT
    ====================================================== -->

    <div class="tab-content">

      <!-- ===================================================
           FRIENDS
      ==================================================== -->

      <div
        v-if="activeTab === 'friends'"
        class="tab-panel"
      >

        <!-- LOADING -->

        <div
          v-if="isLoading"
          class="items-list skeleton-list"
        >
          <FriendSkeleton
            v-for="n in 3"
            :key="`friend-skeleton-${n}`"
          />
        </div>


        <!-- EMPTY -->

        <div
          v-else-if="friends.length === 0"
          class="empty-state"
        >
          <div class="empty-visual">
            <div class="empty-visual-glow"></div>

            <div class="empty-image-wrapper">
              <img
                src="../assets/empty-friends-icon.png"
                alt="Нет друзей"
                class="empty-icon"
              />
            </div>
          </div>

          <div class="empty-badge">
            Здесь пока тихо
          </div>

          <h3 class="empty-title">
            У вас пока нет друзей
          </h3>

          <p class="empty-text">
            Добавляйте интересных людей в друзья,
            чтобы сохранять полезные профессиональные связи.
          </p>
        </div>


        <!-- FRIENDS LIST -->

        <div
          v-else
          class="items-list"
        >
          <FriendItem
            v-for="friend in friends"
            :key="friend.id"
            :friend="friend"
            @remove="handleRemoveFriend"
          />
        </div>

      </div>


      <!-- ===================================================
           REQUESTS
      ==================================================== -->

      <div
        v-else
        class="tab-panel"
      >

        <!-- LOADING -->

        <div
          v-if="isLoading"
          class="items-list skeleton-list"
        >
          <FriendSkeleton
            v-for="n in 3"
            :key="`req-skeleton-${n}`"
          />
        </div>


        <!-- EMPTY -->

        <div
          v-else-if="requests.length === 0"
          class="empty-state"
        >
          <div class="empty-visual">
            <div class="empty-visual-glow requests-glow"></div>

            <div class="empty-image-wrapper">
              <img
                src="../assets/empty-requests-icon.png"
                alt="Нет запросов"
                class="empty-icon"
              />
            </div>
          </div>

          <div class="empty-badge">
            Всё просмотрено
          </div>

          <h3 class="empty-title">
            Новых запросов нет
          </h3>

          <p class="empty-text">
            Когда кто-то захочет добавить вас в друзья,
            новый запрос появится здесь.
          </p>
        </div>


        <!-- REQUESTS LIST -->

        <div
          v-else
          class="items-list"
        >
          <FriendRequest
            v-for="item in requests"
            :key="item.id"
            :request="item"
            @accept="handleAccept"
            @reject="handleReject"
          />
        </div>

      </div>

    </div>

  </div>


  <!-- =====================================================
       SNACKBAR
  ====================================================== -->

  <Snackbar
    :message="snackbarMessage"
    :type="snackbarType"
    :show="snackbarVisible"
  />
</template>


<script setup>

import {
  nextTick,
  onMounted,
  ref
} from "vue";

import {
  waitForAuthReady
} from "../utils/auth.js";

import FriendSkeleton
  from "./FriendSkeleton.vue";

import ConnectPlusService
  from "../api/ConnectPlusService.js";

import FriendRequest
  from "./FriendRequest.vue";

import FriendItem
  from "./FriendItem.vue";

import Snackbar
  from "./Snackbar.vue";


/* ============================================================
   STATE
============================================================ */

const friends =
  ref([]);

const requests =
  ref([]);

const activeTab =
  ref("friends");

const isLoading =
  ref(true);


/* ============================================================
   SNACKBAR
============================================================ */

const snackbarMessage =
  ref("");

const snackbarType =
  ref("success");

const snackbarVisible =
  ref(false);


const showSnackbar =
  async (
    message,
    type = "success"
  ) => {

    snackbarVisible.value =
      false;

    snackbarMessage.value =
      message;

    snackbarType.value =
      type;

    await nextTick();

    snackbarVisible.value =
      true;
  };


/* ============================================================
   LOAD FRIENDS
============================================================ */

const fetchFriends =
  async (
    withLoader = true
  ) => {

    if (withLoader) {
      isLoading.value =
        true;
    }

    try {

      friends.value =
        await ConnectPlusService
          .getFriends();

    } catch (error) {

      console.error(
        "❌ Ошибка загрузки друзей:",
        error
      );

    } finally {

      if (withLoader) {
        isLoading.value =
          false;
      }

    }

  };


/* ============================================================
   LOAD REQUESTS
============================================================ */

const fetchRequests =
  async (
    withLoader = true
  ) => {

    if (withLoader) {
      isLoading.value =
        true;
    }

    try {

      requests.value =
        await ConnectPlusService
          .getFriendRequests();

    } catch (error) {

      console.error(
        "❌ Ошибка загрузки запросов:",
        error
      );

    } finally {

      if (withLoader) {
        isLoading.value =
          false;
      }

    }

  };


/* ============================================================
   ACCEPT REQUEST
============================================================ */

const handleAccept =
  async id => {

    console.log(
      "✅ Принят запрос от:",
      id
    );

    try {

      const success =
        await ConnectPlusService
          .handleFriendRequestAction(
            id,
            "accept"
          );

      if (!success) {

        showSnackbar(
          "Не удалось принять запрос",
          "error"
        );

        return;
      }


      requests.value =
        requests.value.filter(
          request =>
            request.id !== id
        );


      /*
       * Обновляем друзей без переключения
       * всего экрана обратно на skeleton.
       */
      await fetchFriends(
        false
      );


      showSnackbar(
        "Запрос принят 👥",
        "success"
      );

    } catch (error) {

      console.error(
        "❌ Ошибка принятия запроса:",
        error
      );

      showSnackbar(
        "Не удалось принять запрос",
        "error"
      );

    }

  };


/* ============================================================
   REJECT REQUEST
============================================================ */

const handleReject =
  async id => {

    console.log(
      "❌ Отклонён запрос от:",
      id
    );

    try {

      const success =
        await ConnectPlusService
          .handleFriendRequestAction(
            id,
            "decline"
          );

      if (!success) {

        showSnackbar(
          "Не удалось отклонить запрос",
          "error"
        );

        return;
      }


      requests.value =
        requests.value.filter(
          request =>
            request.id !== id
        );


      showSnackbar(
        "Запрос отклонён",
        "success"
      );

    } catch (error) {

      console.error(
        "❌ Ошибка отклонения запроса:",
        error
      );

      showSnackbar(
        "Не удалось отклонить запрос",
        "error"
      );

    }

  };


/* ============================================================
   REMOVE FRIEND
============================================================ */

const handleRemoveFriend =
  async id => {

    try {

      await ConnectPlusService
        .removeFriend(
          id
        );


      friends.value =
        friends.value.filter(
          friend =>
            friend.id !== id
        );


      showSnackbar(
        "Пользователь удалён из друзей",
        "success"
      );

    } catch (error) {

      console.error(
        "❌ Ошибка удаления друга:",
        error
      );

      showSnackbar(
        "Не удалось удалить друга",
        "error"
      );

    }

  };


/* ============================================================
   INITIAL LOAD
============================================================ */

onMounted(
  async () => {

    isLoading.value =
      true;

    try {

      /*
       * Ждём, пока Firebase точно восстановит
       * текущую сессию.
       */
      const user =
        await waitForAuthReady();


      /*
       * Пользователя нет —
       * этот экран ему недоступен.
       */
      if (!user) {

        console.warn(
          "❌ Пользователь не авторизован → редирект на стартовую"
        );

        window.location.replace(
          "https://hr3.space/"
        );

        return;
      }


      /*
       * Друзей и запросы можно грузить
       * одновременно.
       */
      await Promise.all([
        fetchRequests(false),
        fetchFriends(false)
      ]);

    } catch (error) {

      console.error(
        "❌ Ошибка загрузки Connect:",
        error
      );

    } finally {

      isLoading.value =
        false;

    }

  }
);

</script>


<style scoped>

/* ============================================================
   CONTAINER
============================================================ */

.friends-tabs-container {
  position: relative;

  width: 100%;
  max-width: 720px;

  height: 100%;
  min-height: 560px;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;

  overflow: hidden;

  padding:
    26px
    26px
    22px;

  color: #262d37;

  background:
    radial-gradient(
      circle at 8% -10%,
      rgba(77, 139, 236, 0.13),
      transparent 33%
    ),
    radial-gradient(
      circle at 96% 4%,
      rgba(116, 92, 220, 0.08),
      transparent 28%
    ),
    rgba(255, 255, 255, 0.92);

  border:
    1px solid
    rgba(25, 48, 80, 0.075);

  border-radius: 28px;

  box-shadow:
    0 22px 60px
    rgba(24, 39, 66, 0.08);

  backdrop-filter:
    blur(22px);

  -webkit-backdrop-filter:
    blur(22px);
}


.friends-tabs-container::before {
  content: "";

  position: absolute;

  top: -130px;
  left: -90px;

  width: 300px;
  height: 300px;

  pointer-events: none;

  background:
    radial-gradient(
      circle,
      rgba(64, 135, 235, 0.10),
      transparent 67%
    );

  filter:
    blur(18px);
}


/* ============================================================
   HEADER
============================================================ */

.connect-header {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 24px;

  margin-bottom: 24px;
}


.connect-header-copy {
  min-width: 0;
}


.connect-eyebrow {
  display: block;

  margin-bottom: 6px;

  color: #74839a;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1;

  letter-spacing: 0.08em;

  text-transform: uppercase;
}


.connect-title {
  margin: 0;

  color: #252c35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 23px;

  line-height: 1.15;

  letter-spacing: -0.45px;
}


.connect-description {
  max-width: 420px;

  margin:
    7px
    0
    0;

  color: #8b929c;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.5;
}


/* ============================================================
   TOTAL
============================================================ */

.connect-total {
  min-width: 68px;
  min-height: 58px;

  flex-shrink: 0;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    8px
    12px;

  background:
    linear-gradient(
      145deg,
      rgba(242, 247, 255, 0.95),
      rgba(246, 244, 252, 0.95)
    );

  border:
    1px solid
    rgba(65, 110, 185, 0.08);

  border-radius: 17px;
}


.connect-total-value {
  color: #477bd0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;

  line-height: 1;
}


.connect-total-label {
  margin-top: 4px;

  color: #8e97a4;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


/* ============================================================
   TABS
============================================================ */

.tabs-header {
  position: relative;

  z-index: 2;

  width: 100%;

  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  flex-shrink: 0;

  box-sizing: border-box;

  padding: 4px;

  overflow: hidden;

  background:
    rgba(73, 93, 122, 0.055);

  border:
    1px solid
    rgba(25, 48, 80, 0.055);

  border-radius: 18px;
}


/* ============================================================
   SLIDING INDICATOR
============================================================ */

.tab-indicator {
  position: absolute;

  top: 4px;
  bottom: 4px;
  left: 4px;

  z-index: 0;

  width:
    calc(
      50% - 4px
    );

  pointer-events: none;

  background:
    rgba(255, 255, 255, 0.96);

  border:
    1px solid
    rgba(40, 70, 110, 0.065);

  border-radius: 14px;

  box-shadow:
    0 5px 17px
    rgba(32, 55, 90, 0.075);

  transition:
    transform
    0.34s
    cubic-bezier(
      0.22,
      1,
      0.36,
      1
    );
}


.tab-indicator.friends {
  transform:
    translateX(0);
}


.tab-indicator.requests {
  transform:
    translateX(100%);
}


/* ============================================================
   TAB BUTTON
============================================================ */

.tab-button {
  position: relative;

  z-index: 1;

  min-width: 0;
  min-height: 60px;

  display: grid;

  grid-template-columns:
    34px
    minmax(0, 1fr)
    auto;

  align-items: center;

  gap: 10px;

  box-sizing: border-box;

  padding:
    9px
    12px;

  color: #89909a;

  text-align: left;

  background:
    transparent;

  border: none;

  border-radius: 14px;

  cursor: pointer;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}


.tab-button:hover {
  color: #596777;
}


.tab-button.active {
  color: #323a45;
}


.tab-icon {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8294ae;

  background:
    rgba(83, 117, 165, 0.065);

  border-radius: 10px;

  transition:
    color 0.22s ease,
    background-color 0.22s ease,
    transform 0.22s ease;
}


.tab-button.active
.tab-icon {
  color: #477fda;

  background:
    rgba(64, 125, 220, 0.09);

  transform:
    scale(1.03);
}


.tab-icon svg {
  width: 18px;
  height: 18px;
}


.tab-copy {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 3px;
}


.tab-title {
  overflow: hidden;

  color: inherit;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  line-height: 1.2;

  white-space: nowrap;

  text-overflow: ellipsis;
}


.tab-subtitle {
  overflow: hidden;

  color: #a1a7af;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;

  line-height: 1.2;

  white-space: nowrap;

  text-overflow: ellipsis;
}


/* ============================================================
   COUNT
============================================================ */

.tab-count {
  min-width: 25px;
  height: 25px;

  display: inline-flex;

  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    0
    7px;

  color: #7f8997;

  background:
    rgba(85, 105, 135, 0.07);

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease;
}


.tab-button.active
.tab-count {
  color: #477bd1;

  background:
    rgba(55, 120, 215, 0.09);
}


.tab-count.attention {
  color: #3979d8;

  background:
    rgba(50, 120, 225, 0.11);
}


/* ============================================================
   CONTENT
============================================================ */

.tab-content {
  position: relative;

  z-index: 2;

  flex: 1;

  width: 100%;

  min-height: 0;

  box-sizing: border-box;

  padding-top: 18px;

  overflow-y: auto;
  overflow-x: hidden;

  scrollbar-width: none;

  -webkit-overflow-scrolling:
    touch;
}


.tab-content::-webkit-scrollbar {
  display: none;
}


.tab-panel {
  min-height: 100%;
}


.items-list {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 11px;

  box-sizing: border-box;

  padding:
    1px
    2px
    8px;
}


.skeleton-list {
  opacity: 0.9;
}


/* ============================================================
   EMPTY
============================================================ */

.empty-state {
  min-height: 330px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    32px
    24px
    46px;

  text-align: center;
}


.empty-visual {
  position: relative;

  width: 126px;
  height: 126px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 13px;
}


.empty-visual-glow {
  position: absolute;

  inset: 5px;

  background:
    radial-gradient(
      circle,
      rgba(63, 132, 232, 0.16),
      rgba(82, 114, 205, 0.05) 46%,
      transparent 72%
    );

  filter:
    blur(9px);
}


.requests-glow {
  background:
    radial-gradient(
      circle,
      rgba(107, 88, 214, 0.14),
      rgba(67, 127, 220, 0.05) 48%,
      transparent 72%
    );
}


.empty-image-wrapper {
  position: relative;

  z-index: 1;

  width: 92px;
  height: 92px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(250, 252, 255, 0.88);

  border:
    1px solid
    rgba(60, 100, 165, 0.07);

  border-radius: 29px;

  box-shadow:
    0 12px 34px
    rgba(37, 67, 110, 0.07);

  transform:
    rotate(-2deg);
}


.empty-icon {
  width: 72px;
  height: 72px;

  display: block;

  object-fit: contain;
}


.empty-badge {
  min-height: 24px;

  display: inline-flex;

  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    0
    10px;

  margin-bottom: 11px;

  color: #7086a7;

  background:
    rgba(76, 120, 185, 0.065);

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing: 0.025em;
}


.empty-title {
  margin: 0;

  color: #3b424b;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 15px;

  line-height: 1.3;

  letter-spacing: -0.15px;
}


.empty-text {
  max-width: 350px;

  margin:
    8px
    0
    0;

  color: #969da7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.55;
}


/* ============================================================
   DARK THEME
============================================================ */

html[data-theme="dark"]
.friends-tabs-container {
  color: #eef0f4;

  background:
    radial-gradient(
      circle at 8% -10%,
      rgba(70, 125, 215, 0.13),
      transparent 33%
    ),
    radial-gradient(
      circle at 96% 4%,
      rgba(105, 83, 195, 0.09),
      transparent 29%
    ),
    rgba(29, 31, 36, 0.95);

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0 24px 65px
    rgba(0, 0, 0, 0.26);
}


html[data-theme="dark"]
.connect-title {
  color: #f0f2f5;
}


html[data-theme="dark"]
.connect-eyebrow {
  color: #8794a7;
}


html[data-theme="dark"]
.connect-description {
  color: #777f8a;
}


html[data-theme="dark"]
.connect-total {
  background:
    linear-gradient(
      145deg,
      rgba(70, 115, 190, 0.10),
      rgba(100, 80, 175, 0.07)
    );

  border-color:
    rgba(120, 160, 225, 0.09);
}


html[data-theme="dark"]
.connect-total-value {
  color: #91b7ef;
}


html[data-theme="dark"]
.connect-total-label {
  color: #777f8a;
}


html[data-theme="dark"]
.tabs-header {
  background:
    rgba(255, 255, 255, 0.027);

  border-color:
    rgba(255, 255, 255, 0.05);
}


html[data-theme="dark"]
.tab-indicator {
  background:
    linear-gradient(
      145deg,
      rgba(53, 57, 66, 0.97),
      rgba(42, 45, 52, 0.97)
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0 6px 20px
    rgba(0, 0, 0, 0.22);
}


html[data-theme="dark"]
.tab-button {
  color: #777e88;
}


html[data-theme="dark"]
.tab-button:hover {
  color: #b9bec7;
}


html[data-theme="dark"]
.tab-button.active {
  color: #eef0f3;
}


html[data-theme="dark"]
.tab-icon {
  color: #808c9f;

  background:
    rgba(255, 255, 255, 0.035);
}


html[data-theme="dark"]
.tab-button.active
.tab-icon {
  color: #91b8f4;

  background:
    rgba(70, 125, 215, 0.10);
}


html[data-theme="dark"]
.tab-subtitle {
  color: #676e78;
}


html[data-theme="dark"]
.tab-count {
  color: #858e9a;

  background:
    rgba(255, 255, 255, 0.045);
}


html[data-theme="dark"]
.tab-button.active
.tab-count,
html[data-theme="dark"]
.tab-count.attention {
  color: #9dc0f5;

  background:
    rgba(75, 130, 215, 0.10);
}


html[data-theme="dark"]
.empty-image-wrapper {
  background:
    rgba(45, 48, 55, 0.82);

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0 14px 38px
    rgba(0, 0, 0, 0.24);
}


html[data-theme="dark"]
.empty-badge {
  color: #91a8cc;

  background:
    rgba(85, 125, 195, 0.08);
}


html[data-theme="dark"]
.empty-title {
  color: #dadde2;
}


html[data-theme="dark"]
.empty-text {
  color: #7c828b;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {

  .friends-tabs-container {
    max-width: none;

    height: auto;
    min-height: 100%;

    padding:
      20px
      16px
      18px;

    border-radius: 22px;
  }


  .connect-header {
    gap: 12px;

    margin-bottom: 20px;
  }


  .connect-title {
    font-size: 20px;
  }


  .connect-description {
    max-width: 260px;

    font-size: 9px;
  }


  .connect-total {
    min-width: 61px;
    min-height: 54px;

    padding:
      7px
      9px;

    border-radius: 15px;
  }


  .connect-total-value {
    font-size: 16px;
  }


  .tabs-header {
    border-radius: 16px;
  }


  .tab-indicator {
    border-radius: 12px;
  }


  .tab-button {
    min-height: 56px;

    grid-template-columns:
      30px
      minmax(0, 1fr)
      auto;

    gap: 7px;

    padding:
      8px
      8px;
  }


  .tab-icon {
    width: 30px;
    height: 30px;

    border-radius: 9px;
  }


  .tab-icon svg {
    width: 16px;
    height: 16px;
  }


  .tab-title {
    font-size: 9px;
  }


  .tab-subtitle {
    display: none;
  }


  .tab-count {
    min-width: 22px;
    height: 22px;

    padding:
      0
      6px;

    font-size: 7px;
  }


  .tab-content {
    padding-top: 14px;
  }


  .items-list {
    gap: 9px;

    padding-left: 0;
    padding-right: 0;
  }


  .empty-state {
    min-height:
      calc(
        100dvh - 390px
      );

    padding:
      28px
      12px
      36px;
  }


  .empty-visual {
    width: 112px;
    height: 112px;
  }


  .empty-image-wrapper {
    width: 82px;
    height: 82px;

    border-radius: 25px;
  }


  .empty-icon {
    width: 64px;
    height: 64px;
  }


  .empty-title {
    font-size: 14px;
  }


  .empty-text {
    max-width: 280px;

    font-size: 9px;
  }

}


/* ============================================================
   VERY SMALL
============================================================ */

@media (max-width: 390px) {

  .friends-tabs-container {
    padding:
      18px
      13px
      16px;
  }


  .connect-description {
    display: none;
  }


  .connect-header {
    align-items: center;
  }


  .tab-button {
    grid-template-columns:
      28px
      minmax(0, 1fr)
      auto;

    gap: 5px;

    padding:
      7px
      6px;
  }


  .tab-icon {
    width: 28px;
    height: 28px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .tab-indicator,
  .tab-button,
  .tab-icon,
  .tab-count {
    transition: none;
  }

}

</style>